// Company settings → Payment provider. A tenant admin chooses how their payroll /
// loan payouts are sent: the default Bank-file rail (no setup), or their own
// AzamPay merchant account / direct bank (NMB, CRDB) for automated payouts.
// Credentials are write-only (encrypted server-side; never returned to the browser).
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { ActionRefusal } from '@/features/shared/components/ui/ActionRefusal';
import { Banknote, Smartphone, Landmark, ShieldCheck, Info, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  paygateApi, type ProviderConfig, type ProviderCode, type PayoutChannel, type ProviderEnv, type PayoutSettings,
} from '../api/paygateApi';
import { MpesaConnectionsCard } from '../components/MpesaConnectionsCard';

const inputClass =
  'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100';

interface FieldDef {
  key: string; // credential json key
  label: string;
  secret?: boolean;
  type?: 'text' | 'password' | 'textarea' | 'boolean'; // default text; boolean serializes as a real JSON bool
  help?: string;
}

interface ProviderDef {
  code: Exclude<ProviderCode, 'bankfile' | 'mock' | 'mpesa'>;
  name: string;
  icon: typeof Smartphone;
  blurb: string;
  channelChoice: boolean; // AzamPay pays mobile money + bank; direct banks are BANK only
  fixedChannel: PayoutChannel; // used when channelChoice is false
  fields: FieldDef[];
  guide: React.ReactNode[];
}

const PROVIDERS: ProviderDef[] = [
  {
    code: 'azampay',
    name: 'AzamPay',
    icon: Smartphone,
    blurb: 'Pay employees automatically to their mobile-money wallet (Azampesa, Airtel, Mixx/Tigo, Halopesa) or bank, using your own AzamPay merchant account.',
    channelChoice: true,
    fixedChannel: '',
    fields: [
      { key: 'app_name', label: 'App name' },
      { key: 'client_id', label: 'Client ID' },
      { key: 'client_secret', label: 'Client secret', secret: true },
      { key: 'api_key', label: 'API key', secret: true },
    ],
    guide: [
      'Register your business on the AzamPay developer portal and create an app.',
      'Request disbursement (payout) access — separate from collections; needs AzamPay business (KYB) approval.',
      'Copy your App name, Client ID, Client secret and API key from the portal and paste them below.',
      'AzamPay provides the payout signing (checksum) details in their docs once payout access is approved — KaziPeople uses that to sign each payout.',
    ],
  },
  {
    code: 'nmb',
    name: 'NMB Bank',
    icon: Landmark,
    blurb: 'Pay directly from your NMB account using NMB’s Open Bank Project (OBP) API. Salary is debited from your NMB funding account and sent to each employee’s bank account.',
    channelChoice: false,
    fixedChannel: 'BANK',
    fields: [
      { key: 'consumer_key', label: 'Consumer Key', secret: true },
      { key: 'username', label: 'Direct Login username', secret: true },
      { key: 'password', label: 'Direct Login password', secret: true },
      { key: 'source_bank_id', label: 'Funding bank ID (OBP bank_id, e.g. obp1)' },
      { key: 'source_account_id', label: 'Funding account ID (OBP account_id — debited for payouts)' },
      { key: 'request_type', label: 'Transaction type (SANDBOX_TAN for testing, COUNTERPARTY for live)' },
      { key: 'challenge_answer', label: 'Sandbox challenge answer (test only; leave blank in production)', secret: true },
    ],
    guide: [
      'Register a consumer on NMB’s OBP developer portal to get your Consumer Key.',
      'Get your Direct Login username + password (the credentials that authorise payments from your account).',
      'Enter your funding account’s OBP bank_id + account_id — that account is debited for each payout.',
      'For production, set Transaction type to COUNTERPARTY; KaziPeople creates a payee (counterparty) for each employee automatically.',
    ],
  },
  {
    code: 'crdb',
    name: 'CRDB Bank',
    icon: Landmark,
    blurb: 'Pay directly from your CRDB account using CRDB’s payments API.',
    channelChoice: false,
    fixedChannel: 'BANK',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'client_secret', label: 'Client secret', secret: true },
      { key: 'api_key', label: 'API key', secret: true },
      { key: 'source_account', label: 'Your CRDB account number (payouts are debited from this)' },
    ],
    guide: [
      'Ask CRDB to enable their payments / disbursement API for your business account (business agreement + KYB).',
      'Get your API credentials (Client ID, Client secret, API key) from CRDB.',
      'Enter them below along with your CRDB settlement account — that account funds the payouts.',
      'CRDB provides the API signing details; KaziPeople uses them to send each payout.',
    ],
  },
  {
    code: 'selcom',
    name: 'Selcom',
    icon: Smartphone,
    blurb: 'Pay employees to their bank account OR any mobile-money wallet (M-Pesa, Airtel, Mixx/Tigo, Halopesa) through your Selcom merchant account — one connector for all rails.',
    channelChoice: true,
    fixedChannel: '',
    fields: [
      { key: 'api_key', label: 'API Key', secret: true },
      { key: 'api_secret', label: 'API Secret', secret: true },
      { key: 'vendor', label: 'Vendor / Merchant ID (from Selcom onboarding)' },
      { key: 'utility_code', label: 'Disbursement product code (from Selcom)' },
      { key: 'disburse_path', label: 'Disbursement endpoint path (from your Selcom disbursement doc)' },
    ],
    guide: [
      'Onboard with Selcom (info@selcom.net) for disbursement/payout access and get your API Key + API Secret.',
      'Selcom gives you a Vendor/Merchant ID and a disbursement product (Qwiksend for bank, wallet cash-in for mobile money).',
      'Enter the API credentials, your Vendor ID, the product/utility code, and the disbursement endpoint path from your Selcom doc.',
      'KaziPeople signs every payout with the Selcom HMAC digest and sends it to the bank or wallet.',
    ],
  },
  {
    code: 'sftp',
    name: 'Bank SFTP (host-to-host)',
    icon: Landmark,
    blurb:
      'Deliver approved payroll to your bank as a secure file over SFTP (host-to-host). KaziPeople transmits the payment file to the bank and marks each payment paid only when the bank returns confirmation — never just because the file was sent.',
    channelChoice: false,
    fixedChannel: 'BANK',
    fields: [
      { key: 'host', label: 'SFTP host', help: 'The bank\'s SFTP server hostname or IP.' },
      { key: 'port', label: 'Port', help: 'Usually 22.' },
      { key: 'username', label: 'Username' },
      { key: 'password', label: 'Password', secret: true, help: 'Use a password OR an SSH private key below.' },
      { key: 'private_key', label: 'SSH private key (PEM)', type: 'textarea' },
      { key: 'passphrase', label: 'Private key passphrase', secret: true },
      {
        key: 'host_key',
        label: 'Server host key (authorized-keys line)',
        type: 'textarea',
        help: 'Required in production — pins the bank server so no impostor can intercept the payment file.',
      },
      { key: 'remote_upload_dir', label: 'Upload directory', help: 'Where the bank expects the payment file, e.g. /inbound.' },
      {
        key: 'remote_return_dir',
        label: 'Return directory',
        help: 'Where the bank drops the acknowledgement/settlement file, e.g. /outbound. Leave blank if the bank does not return a file.',
      },
      { key: 'file_naming_pattern', label: 'File name pattern', help: 'Use {ts} for a timestamp, e.g. KP_PAYOUT_{ts}.csv.' },
      {
        key: 'pgp_public_key',
        label: "Bank's PGP public key",
        type: 'textarea',
        help: 'If the bank requires the file encrypted, paste its armored PGP public key.',
      },
      { key: 'pgp_required', label: 'Encrypt the payment file with PGP before sending', type: 'boolean' },
      { key: 'insecure_ignore_host_key', label: 'Skip host-key check (sandbox testing only — never enable in production)', type: 'boolean' },
    ],
    guide: [
      'Ask your bank for host-to-host (SFTP) payment-file delivery: the host, port, username, and how to authenticate (a password or an SSH key).',
      'Get the bank server\'s host key so KaziPeople can pin it — production requires this so the file can never be sent to an impostor.',
      'Confirm the upload directory, the return directory for acknowledgements, the file-name format, and whether the file must be PGP-encrypted (and the bank\'s PGP public key).',
      'Paste everything below, start in sandbox, and enable it once your bank confirms a successful test file.',
    ],
  },
];

export const PaymentProviderPage: React.FC = () => {
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // A refused save (e.g. "This address can't be reached from KaziPeople. Use a
  // public HTTPS address.") stays next to the Save button until the user edits.
  const [saveRefusal, setSaveRefusal] = useState<string | null>(null);
  // Per-tenant payout controls: the dual-control verification gate + the sanity caps.
  // Held as one settings object so any save carries the full state (never clobbers caps).
  const [settings, setSettings] = useState<PayoutSettings>({ requirePayoutVerification: false });
  const [verifLoaded, setVerifLoaded] = useState(false);
  const [savingVerif, setSavingVerif] = useState(false);
  // Caps are edited as strings ('' = off) then parsed on save.
  const [caps, setCaps] = useState({ minItem: '', maxItem: '', maxBatch: '', dailyCount: '', dailyAmount: '', currency: '' });
  const [savingCaps, setSavingCaps] = useState(false);
  const requireVerification = settings.requirePayoutVerification;

  const [code, setCode] = useState<ProviderDef['code']>('azampay');
  const [environment, setEnvironment] = useState<ProviderEnv>('sandbox');
  const [channel, setChannel] = useState<PayoutChannel>('');
  const [enabled, setEnabled] = useState(false);
  const [priority, setPriority] = useState('10');
  const [creds, setCreds] = useState<Record<string, string>>({});

  const def = useMemo(() => PROVIDERS.find((p) => p.code === code)!, [code]);
  // The generic card configures WORKSPACE-level connections; per-company ones
  // (e.g. M-Pesa) are managed in their own card.
  const workspaceProviders = useMemo(() => providers.filter((p) => !p.legalEntityId), [providers]);
  const existing = useMemo(() => workspaceProviders.find((p) => p.providerCode === code), [workspaceProviders, code]);

  const load = () =>
    paygateApi
      .listProviders()
      .then(setProviders)
      .catch(() => { /* empty state handles it */ })
      .finally(() => setLoading(false));

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    paygateApi
      .getPayoutSettings()
      .then((s) => {
        setSettings(s);
        setCaps({
          minItem: s.minItemAmount != null ? String(s.minItemAmount) : '',
          maxItem: s.maxItemAmount != null ? String(s.maxItemAmount) : '',
          maxBatch: s.maxBatchTotal != null ? String(s.maxBatchTotal) : '',
          dailyCount: s.dailyCountCap != null ? String(s.dailyCountCap) : '',
          dailyAmount: s.dailyAmountCap != null ? String(s.dailyAmountCap) : '',
          currency: s.capsCurrency ?? '',
        });
      })
      .catch(() => { /* defaults to off */ })
      .finally(() => setVerifLoaded(true));
  }, []);

  const toggleVerification = async (next: boolean) => {
    const prev = settings;
    const nextSettings = { ...settings, requirePayoutVerification: next }; // preserve caps
    setSettings(nextSettings); // optimistic
    setSavingVerif(true);
    try {
      await paygateApi.savePayoutSettings(nextSettings);
      toast.success(next ? 'Payout verification is now required before dispatch.' : 'Payout verification turned off.');
    } catch (e: any) {
      setSettings(prev); // revert on failure
      toast.error(getApiErrorMessage(e, 'Could not update. Admin or Finance access is required.'));
    } finally {
      setSavingVerif(false);
    }
  };

  // Parse a caps input: '' → null (off); otherwise a non-negative integer, or throw.
  const parseCap = (v: string, label: string): number | null => {
    const t = v.trim();
    if (t === '') return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      throw new Error(`${label} must be a whole number (or blank to turn it off).`);
    }
    return n;
  };

  const saveCaps = async () => {
    let next: PayoutSettings;
    try {
      next = {
        ...settings,
        minItemAmount: parseCap(caps.minItem, 'Min per payment'),
        maxItemAmount: parseCap(caps.maxItem, 'Max per payment'),
        maxBatchTotal: parseCap(caps.maxBatch, 'Max per run'),
        dailyCountCap: parseCap(caps.dailyCount, 'Max payments per day'),
        dailyAmountCap: parseCap(caps.dailyAmount, 'Max total per day'),
        capsCurrency: caps.currency.trim().toUpperCase(),
      };
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, e.message));
      return;
    }
    if (next.minItemAmount != null && next.maxItemAmount != null && next.minItemAmount > next.maxItemAmount) {
      toast.error('Min per payment cannot be greater than Max per payment.');
      return;
    }
    const prev = settings;
    setSettings(next); // optimistic
    setSavingCaps(true);
    try {
      await paygateApi.savePayoutSettings(next);
      toast.success('Payout limits saved.');
    } catch (e: any) {
      setSettings(prev); // revert
      toast.error(getApiErrorMessage(e, 'Could not save limits. Admin or Finance access is required.'));
    } finally {
      setSavingCaps(false);
    }
  };

  // When the selected provider (or the loaded configs) change, prefill non-secret
  // settings from the saved config; credential fields stay blank (write-only).
  useEffect(() => {
    const cfg = workspaceProviders.find((p) => p.providerCode === code);
    setEnvironment(cfg?.environment ?? 'sandbox');
    setChannel(def.channelChoice ? (cfg?.channel ?? '') : def.fixedChannel);
    setEnabled(cfg?.enabled ?? false);
    setPriority(String(cfg?.priority ?? 10));
    setCreds({});
    setSaveRefusal(null);
  }, [code, workspaceProviders, def]);

  const setCred = (k: string, v: string) => { setSaveRefusal(null); setCreds((c) => ({ ...c, [k]: v })); };

  const save = async () => {
    const typed = def.fields.some((f) => (creds[f.key] ?? '').trim() !== '');
    if (enabled && !existing?.hasCredentials && !typed) {
      toast.error(`Enter your ${def.name} credentials before enabling it.`);
      return;
    }
    setSaving(true);
    setSaveRefusal(null);
    try {
      const credentials = typed
        ? Object.fromEntries(def.fields.map((f) => [
            f.key,
            f.type === 'boolean' ? creds[f.key] === 'true' : (creds[f.key] ?? '').trim(),
          ]))
        : null;
      await paygateApi.saveProvider(code, {
        channel: def.channelChoice ? channel : def.fixedChannel,
        enabled,
        environment,
        priority: Number(priority) || 10,
        credentials,
      });
      toast.success(`${def.name} settings saved.`);
      setCreds({});
      await load();
    } catch (e: any) {
      setSaveRefusal(getApiErrorMessage(e, 'Could not save. Admin or Finance access is required.'));
    } finally {
      setSaving(false);
    }
  };

  const statusFor = (c: ProviderCode) => workspaceProviders.find((p) => p.providerCode === c);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">Payment provider</h1>
        <p className="text-muted-foreground mt-1">
          Choose how salary, loan, and advance payouts leave your company. This applies to every payout across payroll and loans.
        </p>
      </div>

      {/* How it works */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-orange-500" />
            <h2 className="font-semibold text-foreground">How payouts work</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Every payout runs through KaziPeople&rsquo;s payout engine, which sends the money using a <strong>connector</strong> you
            choose. Money is never held by KaziPeople — it moves straight from your provider to your employees.
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2"><Banknote className="mt-0.5 h-4 w-4 flex-shrink-0 text-charcoal" /><span><strong className="text-foreground">Bank file (default).</strong> No setup — download a payment file and upload it to your bank. Works today.</span></li>
            <li className="flex gap-2"><Smartphone className="mt-0.5 h-4 w-4 flex-shrink-0 text-charcoal" /><span><strong className="text-foreground">Mobile money (M-Pesa per company; AzamPay, Selcom).</strong> Automated payouts to mobile-money wallets — and, via Selcom, banks too — through your own merchant account.</span></li>
            <li className="flex gap-2"><Landmark className="mt-0.5 h-4 w-4 flex-shrink-0 text-charcoal" /><span><strong className="text-foreground">Direct bank (NMB, CRDB).</strong> Pay straight from your own bank account via the bank&rsquo;s API.</span></li>
          </ul>
        </CardContent>
      </Card>

      {/* Payout verification gate (dual control) */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-orange-500" />
            <h2 className="font-semibold text-foreground">Payout verification</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Add a final human check before money leaves your company. When on, every payout batch — payroll, loans and
            advances — is <strong>held</strong> until an owner or finance user verifies and releases it on the Payouts page.
            Once released, a payout is sent and cannot be held again.
          </p>
          <label className="flex items-start gap-3 rounded-lg border border-border px-3 py-3">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={requireVerification}
              disabled={!verifLoaded || savingVerif}
              onChange={(e) => toggleVerification(e.target.checked)}
            />
            <span className="text-sm text-foreground">
              Require verification before payouts are sent
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Recommended when the person who prepares payroll is not the person who approves the money leaving.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      {/* Payout limits (sanity caps) */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-orange-500" />
            <h2 className="font-semibold text-foreground">Payout limits</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            A safety belt against a wrong amount (an extra zero) or a runaway run. A payout that exceeds any limit is
            <strong> held for verification</strong> on the Payouts page instead of being sent — it is never blocked
            outright. Leave a field blank to turn that limit off. Amounts are in whole {caps.currency || 'currency'} units.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {([
              ['minItem', 'Min per payment', 'Hold if any single employee payment is below this (catches a zero or a mis-key)'],
              ['maxItem', 'Max per payment', 'Hold if any single employee payment is above this'],
              ['maxBatch', 'Max per run', 'Hold if a batch total is above this'],
              ['dailyCount', 'Max payments per day', 'Hold if the trailing-24h payout count would exceed this'],
              ['dailyAmount', 'Max total per day', 'Hold if the trailing-24h payout total would exceed this'],
            ] as const).map(([key, label, hint]) => (
              <label key={key} className="block">
                <span className="text-sm font-medium text-foreground">{label}</span>
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm tabular-nums text-foreground"
                  placeholder="Off"
                  value={caps[key]}
                  disabled={!verifLoaded || savingCaps}
                  onChange={(e) => setCaps((c) => ({ ...c, [key]: e.target.value }))}
                />
                <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
              </label>
            ))}
            <label className="block">
              <span className="text-sm font-medium text-foreground">Limits currency</span>
              <input
                type="text"
                maxLength={3}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm uppercase text-foreground"
                placeholder="Any"
                value={caps.currency}
                disabled={!verifLoaded || savingCaps}
                onChange={(e) => setCaps((c) => ({ ...c, currency: e.target.value }))}
              />
              <span className="mt-0.5 block text-xs text-muted-foreground">Only check runs in this currency (blank = any).</span>
            </label>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveCaps} disabled={!verifLoaded || savingCaps}>{savingCaps ? 'Saving…' : 'Save limits'}</Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          {/* Providers at a glance */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <h2 className="font-semibold text-foreground">Your providers</h2>
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div className="flex items-center gap-2"><Banknote className="h-4 w-4 text-charcoal" /><span className="text-sm font-medium text-foreground">Bank file</span><Badge variant="secondary">Default</Badge></div>
                  <Badge variant="success">Active</Badge>
                </div>
                {PROVIDERS.map((p) => {
                  const s = statusFor(p.code);
                  return (
                    <div key={p.code} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <p.icon className="h-4 w-4 text-charcoal" />
                        <span className="text-sm font-medium text-foreground">{p.name}</span>
                        {s?.enabled ? <Badge variant="info">Enabled · {s.environment}</Badge> : s?.hasCredentials ? <Badge variant="secondary">Saved</Badge> : <Badge variant="secondary">Not connected</Badge>}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setCode(p.code)}>Configure</Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Configure a provider */}
          <Card>
            <CardContent className="p-5 space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <def.icon className="mt-0.5 h-5 w-5 text-charcoal" />
                  <div>
                    <span className="font-semibold text-foreground">{def.name}</span>
                    <p className="mt-1 text-sm text-muted-foreground">{def.blurb}</p>
                  </div>
                </div>
                {existing?.hasCredentials ? <Badge variant="info">Credentials saved</Badge> : <Badge variant="secondary">Not connected</Badge>}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Provider</label>
                <select className={inputClass} value={code} onChange={(e) => setCode(e.target.value as ProviderDef['code'])}>
                  {PROVIDERS.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                </select>
              </div>

              {/* Connection guide */}
              <div className="rounded-lg border border-border bg-muted/40 p-4">
                <p className="mb-2 text-sm font-medium text-foreground">How to connect {def.name}</p>
                <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
                  {def.guide.map((g, i) => <li key={i}>{g}</li>)}
                  <li>Save here — we store your credentials encrypted. Automated payouts switch on once {def.name} is enabled for your company.</li>
                </ol>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Environment</label>
                  <select className={inputClass} value={environment} onChange={(e) => setEnvironment(e.target.value as ProviderEnv)}>
                    <option value="sandbox">Sandbox (testing)</option>
                    <option value="live">Production (live money)</option>
                  </select>
                </div>
                {def.channelChoice ? (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Pay to</label>
                    <select className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value as PayoutChannel)}>
                      <option value="">Mobile money &amp; bank</option>
                      <option value="MOBILE_MONEY">Mobile money only</option>
                      <option value="BANK">Bank only</option>
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Pay to</label>
                    <div className={`${inputClass} text-muted-foreground`}>Bank accounts</div>
                  </div>
                )}
                {def.fields.map((f) =>
                  f.type === 'boolean' ? (
                    <label key={f.key} className="flex items-start gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={creds[f.key] === 'true'}
                        onChange={(e) => setCred(f.key, e.target.checked ? 'true' : '')}
                      />
                      <span>{f.label}</span>
                    </label>
                  ) : (
                    <div key={f.key}>
                      <label className="mb-1 block text-sm font-medium text-foreground">{f.label}</label>
                      {f.type === 'textarea' ? (
                        <textarea
                          className={inputClass}
                          rows={4}
                          value={creds[f.key] ?? ''}
                          onChange={(e) => setCred(f.key, e.target.value)}
                          placeholder={existing?.hasCredentials ? '•••••• (saved — leave blank to keep)' : `from ${def.name}`}
                        />
                      ) : (
                        <input
                          type={f.secret || f.type === 'password' ? 'password' : 'text'}
                          className={inputClass}
                          value={creds[f.key] ?? ''}
                          onChange={(e) => setCred(f.key, e.target.value)}
                          placeholder={existing?.hasCredentials ? '•••••• (saved — leave blank to keep)' : `from ${def.name}`}
                          autoComplete={f.secret ? 'new-password' : 'off'}
                        />
                      )}
                      {f.help ? <p className="mt-1 text-xs text-muted-foreground">{f.help}</p> : null}
                    </div>
                  ),
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                Use {def.name} for payouts (when enabled + approved, payouts route here instead of the bank file)
              </label>

              <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" />
                Your credentials are encrypted and write-only — never shown again after saving. Enter new values only to replace them.
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {existing?.hasCredentials ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-success" /> Credentials on file.</span> : 'No credentials saved yet.'}
                </span>
                <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : `Save ${def.name} settings`}</Button>
              </div>
              <ActionRefusal message={saveRefusal} onDismiss={() => setSaveRefusal(null)} testId="provider-save-refusal" />
            </CardContent>
          </Card>

          {/* M-Pesa: one business account per company (Increment D) */}
          <MpesaConnectionsCard />

          <p className="text-center text-xs text-muted-foreground">
            Bank payments without a connection continue via the bank file. Mobile-money payments need a connection for
            the paying company — without one they are stopped with a clear reason, never sent another way.
          </p>
        </>
      )}
    </div>
  );
};

export default PaymentProviderPage;
